"use client"

/**
 * Picks what an escalation does to the AI. Lives in `shared/` because the email
 * settings tab gets the same control once email joins the pause policy.
 */

import { useId } from "react"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DEFAULT_HANDOVER_PAUSE_MODE,
  type HandoverPauseMode,
} from "@/lib/ai/tools/human-handover/handover-config"
import { useTranslations } from "next-intl"


function getOPTIONS(tUi: ReturnType<typeof useTranslations<"AgentTools.AgentsSharedHandoverPauseModeSelect">>): { value: HandoverPauseMode; label: string; hint: string }[] {
  return [
  {
    value: "always",
    label: tUi("alwaysPauseTheAI"),
    hint: tUi("theAIStopsReplyingAndWaitsForA"),
  },
  {
    value: "never",
    label: tUi("neverPauseTheAI"),
    hint: tUi("theAIKeepsAnsweringWhileYourTeamIs"),
  },
  {
    value: "ai_decides",
    label: tUi("letTheAIDecide"),
    hint: tUi("theAIPausesOnlyWhenCarryingOnWould"),
  },
]
}

interface HandoverPauseModeSelectProps {
  value?: HandoverPauseMode
  onChange: (value: HandoverPauseMode) => void
  disabled?: boolean
}

export function HandoverPauseModeSelect({
  value,
  onChange,
  disabled,
}: HandoverPauseModeSelectProps) {
  const tUi = useTranslations("AgentTools.AgentsSharedHandoverPauseModeSelect")
  const OPTIONS = getOPTIONS(tUi)


  const selectId = useId()
  const selected = value ?? DEFAULT_HANDOVER_PAUSE_MODE

  return (
    <div className="space-y-1.5">
      <Label htmlFor={selectId} className="text-sm font-medium">
        <span key={tUi("whenTheAIHandsOver")}>{tUi("whenTheAIHandsOver")}</span></Label>
      <Select
        value={selected}
        onValueChange={(next) => onChange(next as HandoverPauseMode)}
        disabled={disabled}
      >
        <SelectTrigger id={selectId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {OPTIONS.find((o) => o.value === selected)?.hint}
      </p>
    </div>
  )
}
